import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { dataManagementService } from '../../../services/dataManagementService';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import ClearDataModal from '../../individual-practitioner-dashboard/components/ClearDataModal';

const SecurityTab = () => {
  const { user, updatePassword, updateEmail, userProfile } = useAuth();
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  
  // Email change state
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    confirmEmail: ''
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);

  // Clear data state
  const [showClearDataModal, setShowClearDataModal] = useState(false);
  const [clearDataLoading, setClearDataLoading] = useState(false);
  const [clearDataSuccess, setClearDataSuccess] = useState(false);
  const [clearDataError, setClearDataError] = useState('');

  // Password validation
  const validatePassword = (password) => {
    if (password?.length < 8) {
      return 'Le mot de passe doit contenir au moins 8 caractères';
    }
    if (!/[A-Z]/?.test(password)) {
      return 'Le mot de passe doit contenir au moins une majuscule';
    }
    if (!/[a-z]/?.test(password)) {
      return 'Le mot de passe doit contenir au moins une minuscule';
    }
    if (!/[0-9]/?.test(password)) {
      return 'Le mot de passe doit contenir au moins un chiffre';
    }
    return null;
  };

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) {
      return 'Adresse email invalide';
    }
    return null;
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e?.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    // Validation
    if (!passwordForm?.currentPassword) {
      setPasswordError('Veuillez entrer votre mot de passe actuel');
      return;
    }

    if (!passwordForm?.newPassword) {
      setPasswordError('Veuillez entrer un nouveau mot de passe');
      return;
    }

    const passwordValidation = validatePassword(passwordForm?.newPassword);
    if (passwordValidation) {
      setPasswordError(passwordValidation);
      return;
    }

    if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordForm?.currentPassword === passwordForm?.newPassword) {
      setPasswordError('Le nouveau mot de passe doit être différent de l\'ancien');
      return;
    }

    try {
      setPasswordLoading(true);
      
      const { error } = await updatePassword(passwordForm?.newPassword);
      
      if (error) {
        throw error;
      }

      setPasswordSuccess(true);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (error) {
      console.error('Password update error:', error);
      setPasswordError(error?.message || 'Erreur lors de la modification du mot de passe');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Handle email change
  const handleEmailChange = async (e) => {
    e?.preventDefault();
    setEmailError('');
    setEmailSuccess(false);

    // Validation
    if (!emailForm?.newEmail) {
      setEmailError('Veuillez entrer une nouvelle adresse email');
      return;
    }

    const emailValidation = validateEmail(emailForm?.newEmail);
    if (emailValidation) {
      setEmailError(emailValidation);
      return;
    }

    if (emailForm?.newEmail !== emailForm?.confirmEmail) {
      setEmailError('Les adresses email ne correspondent pas');
      return;
    }

    if (emailForm?.newEmail === user?.email) {
      setEmailError('La nouvelle adresse email doit être différente de l\'actuelle');
      return;
    }

    try {
      setEmailLoading(true);
      
      const { error } = await updateEmail(emailForm?.newEmail);
      
      if (error) {
        throw error;
      }

      setEmailSuccess(true);
      setEmailForm({
        newEmail: '',
        confirmEmail: ''
      });

      setTimeout(() => setEmailSuccess(false), 5000);
    } catch (error) {
      console.error('Email update error:', error);
      setEmailError(error?.message || 'Erreur lors de la modification de l\'adresse email');
    } finally {
      setEmailLoading(false);
    }
  };

  // Handle clear all data
  const handleClearAllData = async () => {
    try {
      setClearDataLoading(true);
      setClearDataError('');
      setClearDataSuccess(false);

      const result = await dataManagementService?.deletePractitionerReferrals(userProfile?.practitionerId);

      if (result?.success) {
        setClearDataSuccess(true);
        setShowClearDataModal(false);
        
        // Notify parent component to reload data
        window.dispatchEvent(new CustomEvent('referrals-cleared'));

        setTimeout(() => setClearDataSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error clearing data:', error);
      setClearDataError(error?.message || 'Erreur lors de la suppression des données');
    } finally {
      setClearDataLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-primary mb-2">Sécurité du compte</h2>
        <p className="text-muted">Gérez votre mot de passe et adresse email de connexion</p>
      </div>
      {/* Current Email Display */}
      <div className="bg-card-elevated rounded-xl p-6 border border-white/8">
        <div className="flex items-center gap-3 mb-2">
          <Icon name="Mail" size={20} className="text-accent" />
          <h3 className="text-lg font-semibold text-primary">Email actuel</h3>
        </div>
        <p className="text-secondary font-medium">{user?.email}</p>
      </div>
      {/* Password Change Form */}
      <div className="bg-card-elevated rounded-xl p-6 border border-white/8">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Lock" size={20} className="text-accent" />
          <h3 className="text-lg font-semibold text-primary">Modifier le mot de passe</h3>
        </div>

        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              Mot de passe actuel *
            </label>
            <Input
              type="password"
              value={passwordForm?.currentPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e?.target?.value }))}
              placeholder="Entrez votre mot de passe actuel"
              disabled={passwordLoading}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              Nouveau mot de passe *
            </label>
            <Input
              type="password"
              value={passwordForm?.newPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e?.target?.value }))}
              placeholder="Minimum 8 caractères, 1 majuscule, 1 minuscule, 1 chiffre"
              disabled={passwordLoading}
              className="w-full"
            />
            <p className="text-xs text-muted mt-1">
              Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              Confirmer le nouveau mot de passe *
            </label>
            <Input
              type="password"
              value={passwordForm?.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
              placeholder="Confirmez votre nouveau mot de passe"
              disabled={passwordLoading}
              className="w-full"
            />
          </div>

          {passwordError && (
            <div className="bg-error-bg border border-error/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-secondary">{passwordError}</p>
            </div>
          )}

          {passwordSuccess && (
            <div className="bg-success-bg border border-success/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <p className="text-sm text-secondary">Mot de passe modifié avec succès</p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={passwordLoading}
              className="px-6 py-2 bg-accent hover:bg-accent-hover text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {passwordLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Modification...</span>
                </>
              ) : (
                <>
                  <Icon name="Save" size={18} />
                  <span>Modifier le mot de passe</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
      {/* Email Change Form */}
      <div className="bg-card-elevated rounded-xl p-6 border border-white/8">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="AtSign" size={20} className="text-accent" />
          <h3 className="text-lg font-semibold text-primary">Modifier l'adresse email</h3>
        </div>

        <form onSubmit={handleEmailChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              Nouvelle adresse email *
            </label>
            <Input
              type="email"
              value={emailForm?.newEmail}
              onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e?.target?.value }))}
              placeholder="nouvelle@email.com"
              disabled={emailLoading}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              Confirmer la nouvelle adresse email *
            </label>
            <Input
              type="email"
              value={emailForm?.confirmEmail}
              onChange={(e) => setEmailForm(prev => ({ ...prev, confirmEmail: e?.target?.value }))}
              placeholder="Confirmez votre nouvelle adresse email"
              disabled={emailLoading}
              className="w-full"
            />
          </div>

          {emailError && (
            <div className="bg-error-bg border border-error/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-secondary">{emailError}</p>
            </div>
          )}

          {emailSuccess && (
            <div className="bg-success-bg border border-success/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <p className="text-sm text-secondary">Email modifié avec succès. Veuillez vérifier votre nouvelle adresse email pour confirmer le changement.</p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={emailLoading}
              className="px-6 py-2 bg-accent hover:bg-accent-hover text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {emailLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Modification...</span>
                </>
              ) : (
                <>
                  <Icon name="Save" size={18} />
                  <span>Modifier l'email</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Data Management Section */}
      <div className="bg-card-elevated rounded-xl p-6 border border-white/8">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Database" size={20} className="text-accent" />
          <h3 className="text-lg font-semibold text-primary">Gestion des données</h3>
        </div>

        <div className="space-y-4">
          <div className="bg-warning-bg border border-warning/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-primary mb-1">Supprimer tous les adressages</h4>
                <p className="text-sm text-secondary mb-3">
                  Cette action supprimera définitivement tous vos adressages de patients. 
                  Utilisez cette fonction si vous souhaitez repartir de zéro avec une base de données vide.
                </p>
                <p className="text-xs text-muted">
                  ⚠️ Cette action est irréversible et ne peut pas être annulée.
                </p>
              </div>
            </div>
          </div>

          {clearDataSuccess && (
            <div className="bg-success-bg border border-success/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <p className="text-sm text-secondary">Tous les adressages ont été supprimés avec succès</p>
            </div>
          )}

          {clearDataError && (
            <div className="bg-error-bg border border-error/30 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-secondary">{clearDataError}</p>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={() => setShowClearDataModal(true)}
              disabled={clearDataLoading}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Icon name="Trash2" size={18} />
              <span>Supprimer tous les adressages</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Clear Data Modal */}
      <ClearDataModal
        isOpen={showClearDataModal}
        onClose={() => setShowClearDataModal(false)}
        onConfirm={handleClearAllData}
        practitionerName={userProfile?.fullName || 'ce praticien'}
        loading={clearDataLoading}
      />
      {/* Security Tips */}
      <div className="bg-card-elevated rounded-xl p-6 border border-white/8">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="Shield" size={20} className="text-accent" />
          <h3 className="text-lg font-semibold text-primary">Conseils de sécurité</h3>
        </div>
        <ul className="space-y-2 text-sm text-muted">
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
            <span>Utilisez un mot de passe unique pour ce compte</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
            <span>Changez votre mot de passe régulièrement (tous les 3-6 mois)</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
            <span>Ne partagez jamais votre mot de passe avec qui que ce soit</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
            <span>Utilisez une adresse email professionnelle et sécurisée</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SecurityTab;